import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ScheduledActivity } from '../../interfaces/activity-schedule.interface';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-dashboard-calendar-widget',
  templateUrl: './dashboard-calendar-widget.component.html',
  styleUrls: ['./dashboard-calendar-widget.component.scss']
})
export class DashboardCalendarWidgetComponent implements OnInit, OnDestroy {
  @Input() maxItems: number = 5;
  @Input() showTitle: boolean = true;
  
  private destroy$ = new Subject<void>();
  
  upcomingActivities: ScheduledActivity[] = [];
  todayActivities: ScheduledActivity[] = [];
  isLoading = false;
  currentUserId = 0;
  
  statusOptions = this.activityScheduleService.getStatusOptions();

  constructor(
    private activityScheduleService: ActivityScheduleService,
    private authService: AuthService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadUserContext();
    this.loadUpcomingActivities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadUserContext(): Promise<void> {
    try {
      const user = await this.authService.getCurrentUser();
      if (user && user.id_user) {
        this.currentUserId = user.id_user;
      } else {
        this.currentUserId = 1; // Fallback
      }
    } catch (error) {
      console.error('Error loading current user:', error);
      this.currentUserId = 1; // Fallback
    }
  }

  private loadUpcomingActivities(): void {
    if (!this.currentUserId) return;
    
    this.isLoading = true;
    
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);
    
    const startDate = this.formatDateForAPI(today);
    const endDate = this.formatDateForAPI(nextWeek);

    this.activityScheduleService.getCalendarActivities(this.currentUserId, startDate, endDate).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (activities) => {
        // Filter only scheduled and rescheduled activities
        const relevantActivities = activities.filter(activity => 
          ['scheduled', 'rescheduled'].includes(activity.status)
        );
        
        // Sort by date and time
        relevantActivities.sort((a, b) => {
          const dateCompare = a.scheduled_date.localeCompare(b.scheduled_date);
          if (dateCompare === 0) {
            return a.start_time.localeCompare(b.start_time);
          }
          return dateCompare;
        });

        this.upcomingActivities = relevantActivities.slice(0, this.maxItems);
        
        // Filter today's activities
        const todayStr = this.formatDateForAPI(today);
        this.todayActivities = relevantActivities.filter(activity => 
          activity.scheduled_date === todayStr
        );
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading upcoming activities:', error);
        this.isLoading = false;
      }
    });
  }

  private formatDateForAPI(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  navigateToCalendar(): void {
    this.router.navigate(['/programari']);
  }

  navigateToActivity(activity: ScheduledActivity): void {
    // Navigate to calendar on the specific date
    this.router.navigate(['/programari'], {
      queryParams: {
        date: activity.scheduled_date,
        view: 'day'
      }
    });
  }

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.key === status);
    return option?.label || status;
  }

  getStatusColor(status: string): string {
    const option = this.statusOptions.find(opt => opt.key === status);
    return option?.color || '#6c757d';
  }

  formatTime(time: string): string {
    return time.substring(0, 5); // HH:MM from HH:MM:SS
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateString = date.toDateString();
    const todayString = today.toDateString();
    const tomorrowString = tomorrow.toDateString();

    if (dateString === todayString) {
      return 'Astăzi';
    } else if (dateString === tomorrowString) {
      return 'Mâine';
    } else {
      return date.toLocaleDateString('ro-RO', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    }
  }

  trackByActivityId(index: number, activity: ScheduledActivity): number {
    return activity.id;
  }

  getTimeUntilActivity(activity: ScheduledActivity): string {
    const now = new Date();
    const activityDateTime = new Date(`${activity.scheduled_date}T${activity.start_time}`);
    const diffMs = activityDateTime.getTime() - now.getTime();
    
    if (diffMs < 0) return 'În desfășurare';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 24) {
      const diffDays = Math.floor(diffHours / 24);
      return `în ${diffDays} zi${diffDays > 1 ? 'le' : ''}`;
    } else if (diffHours > 0) {
      return `în ${diffHours}h ${diffMinutes}min`;
    } else if (diffMinutes > 0) {
      return `în ${diffMinutes} min`;
    } else {
      return 'Acum';
    }
  }

  get todayCount(): number {
    return this.todayActivities.length;
  }

  get hasUpcomingActivities(): boolean {
    return this.upcomingActivities.length > 0;
  }

  refreshActivities(): void {
    this.loadUpcomingActivities();
  }
}